import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Link, useLocation } from 'react-router-dom'
import { useLanguage } from '../Context/LanguageContext'
import LanguageSwitcher from '../Components/LanguageSwitcher/LanguageSwitcher'
import logo from '../Assets/img/imigo-logo.png'

const APP_STORE_URL = 'https://apps.apple.com/us/app/imigo-connect/id6744267446'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.imigo'
const RESET_API = 'https://panel.imigo.app/api/auth/reset-password'

const readQueryValue = (search, hash, keys) => {
  const sources = [search, hash.startsWith('#') ? hash.slice(1) : hash]
  for (const source of sources) {
    if (!source) continue
    const params = new URLSearchParams(source)
    for (const key of keys) {
      const value = params.get(key)
      if (value) return value
    }
  }
  return ''
}

const ResetPassword = () => {
  const location = useLocation()
  const { getLocalizedPath, t } = useLanguage()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const token = useMemo(
    () => readQueryValue(location.search, location.hash, ['code', 'token', 'resetPasswordToken']),
    [location.search, location.hash]
  )

  useEffect(() => {
    document.title = `${t('resetPassword.title')} | Imigo`
  }, [t])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setMessage('')
    setError('')

    if (password !== confirmPassword) {
      setError(t('resetPassword.mismatch'))
      return
    }

    if (password.length < 6) {
      setError(t('resetPassword.tooShort'))
      return
    }

    if (!token) {
      setError(t('resetPassword.invalidLink'))
      return
    }

    try {
      setSubmitting(true)
      await axios.post(RESET_API, {
        password,
        passwordConfirmation: confirmPassword,
        code: token,
      })
      setMessage(t('resetPassword.success'))
    } catch (err) {
      const apiMessage = err.response?.data?.error?.message
      setError(apiMessage || t('resetPassword.failed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="imigo-auth-page imigoAnimationBg">
      <div className="imigo-auth-toolbar">
        <LanguageSwitcher variant="header" />
      </div>

      <div className="imigo-auth-card">
        <Link to={getLocalizedPath('/')} className="imigo-auth-logo" aria-label="Imigo">
          <img src={logo} alt="Imigo" />
        </Link>

        <h1>{t('resetPassword.title')}</h1>
        <p className="imigo-auth-lead">{t('resetPassword.subtitle')}</p>

        {!token && (
          <div className="alert-warning-imigo" role="alert">
            {t('resetPassword.missingCode')}
          </div>
        )}

        {message ? (
          <div className="imigo-auth-success">
            <p className="alert-success-imigo">{message}</p>
            <a href={APP_STORE_URL} className="btn-imigo-primary" target="_blank" rel="noopener noreferrer">
              {t('common.onAppStore')}
            </a>
            <a href={PLAY_STORE_URL} className="btn-imigo-secondary" target="_blank" rel="noopener noreferrer">
              {t('common.onGooglePlay')}
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <label className="imigo-auth-label" htmlFor="new-password">
              {t('resetPassword.newPassword')}
            </label>
            <div className="imigo-auth-field">
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                className="form-control-imigo"
                placeholder={t('resetPassword.newPasswordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={6}
              />
              <button
                type="button"
                className="imigo-auth-eye"
                onClick={() => setShowPassword((open) => !open)}
                aria-label={showPassword ? t('resetPassword.hidePassword') : t('resetPassword.showPassword')}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
              </button>
            </div>

            <label className="imigo-auth-label" htmlFor="confirm-password">
              {t('resetPassword.confirmPassword')}
            </label>
            <div className="imigo-auth-field">
              <input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                className="form-control-imigo"
                placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={6}
              />
            </div>

            <button type="submit" className="btn-imigo-primary" disabled={submitting}>
              {submitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
            </button>
          </form>
        )}

        {error && <p className="alert-error-imigo" role="alert">{error}</p>}

        <Link to={getLocalizedPath('/')} className="imigo-auth-home">
          {t('common.backHome')}
        </Link>
      </div>
    </div>
  )
}

export default ResetPassword
